import React, { useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import classes from "../MasterDashboard.module.css";
import StatTile from "../shared/StatTile";
import { VEHICLE_STATUS_ORDER, VEHICLE_STATUS_META } from "../shared/vehicleStatusMeta";
import { exportVehicleAllocationTable } from "../utils/exportVehicleAllocation";

const MATRIX_TOP_N = 8;
const TOP_N = 10;
const LANE_TRUNCATE_LEN = 40;
const truncateLane = (text) => (text && text.length > LANE_TRUNCATE_LEN ? `${text.slice(0, LANE_TRUNCATE_LEN)}…` : text);

const locationName = (loc) => {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  return loc.location?.label || loc.location_name || loc.location_code || "";
};
const locationHoverText = (loc) => {
  if (!loc || typeof loc === "string") return loc || undefined;
  return [loc.addressLine1 || loc.location_name, loc.location_code].filter(Boolean).join(" | ") || undefined;
};

const badgeClassForStatus = (status) => {
  const cssKey = VEHICLE_STATUS_META[status]?.cssKey;
  const badgeClass = cssKey && classes[`badge${cssKey[0].toUpperCase()}${cssKey.slice(1)}`];
  return badgeClass || classes.pillMuted;
};

const StatusTilesRow = ({ statusTiles }) => (
  <div className={classes.statRow7}>
    {VEHICLE_STATUS_ORDER.map((status) => (
      <StatTile
        key={status}
        icon={VEHICLE_STATUS_META[status].icon}
        label={VEHICLE_STATUS_META[status].label.toUpperCase()}
        value={statusTiles[status] || 0}
        labelClassName={status === "paymentapproved" ? "statLabelSmall" : undefined}
      />
    ))}
  </div>
);

const MATRIX_SHADE_STEPS = [
  { min: 3, className: "matrixShade3" },
  { min: 2, className: "matrixShade2" },
  { min: 1, className: "matrixShade1" },
];

const matrixCellClass = (value) => {
  if (!value) return classes.matrixCellEmpty;
  const step = MATRIX_SHADE_STEPS.find((s) => value >= s.min) || MATRIX_SHADE_STEPS[MATRIX_SHADE_STEPS.length - 1];
  return `${classes.matrixCell} ${classes[step.className]}`;
};

const MatrixTable = ({ destinations, rows, totals, grandTotal }) => (
  <div className={classes.tableWrap}>
    <table className={`${classes.table} ${classes.matrixTable}`}>
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>ORIGIN ↓ / DEST →</th>
          {destinations.map((dest) => (
            <th
              key={dest.key}
              className={classes.matrixDestHeader}
              style={{ textAlign: "left" }}
              title={[dest.address, dest.code].filter(Boolean).join(" | ") || dest.name}
            >
              {dest.name}
            </th>
          ))}
          <th style={{ textAlign: "center", width: 56 }}>Vehicles</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td
              className={classes.plantCell}
              title={[row.address, row.code].filter(Boolean).join(" | ") || row.name}
              style={{ textAlign: "left" }}
            >
              {row.name}
            </td>
            {destinations.map((dest) => {
              const value = row.cells[dest.key] || 0;
              return (
                <td key={dest.key} style={{ padding: 0 }}>
                  <div className={matrixCellClass(value)} title={value ? `${row.name} → ${dest.name}: ${value}` : undefined}>
                    {value || "·"}
                  </div>
                </td>
              );
            })}
            <td style={{ textAlign: "center", fontWeight: 700 }}>{row.total}</td>
          </tr>
        ))}
        <tr>
          <td className={classes.plantCell}>Total</td>
          {destinations.map((dest) => (
            <td key={dest.key} style={{ textAlign: "center" }}>
              {totals[dest.key] || 0}
            </td>
          ))}
          <td style={{ textAlign: "center", fontWeight: 700 }}>{grandTotal}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

const downloadDispatchMatrix = (rows, destinations) => {
  const destNameByKey = new Map(destinations.map((d) => [d.key, d.name]));
  const exportRows = [];
  rows.forEach((row) => {
    Object.entries(row.cells).forEach(([destKey, count]) => {
      if (count) {
        exportRows.push({
          Origin: row.name,
          Destination: destNameByKey.get(destKey) || destKey,
          "Vehicles Dispatched": count,
        });
      }
    });
  });

  if (!exportRows.length) {
    toast.error("Data is not available to download");
    return;
  }

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Dispatch Matrix");
  XLSX.writeFile(wb, "dispatch_matrix.xlsx");
};

const DispatchMatrix = ({ destinations, rows, totals, grandTotal, viewOnly = false, onRestrictedClick = () => {} }) => {
  const [expanded, setExpanded] = useState(false);

  const sortedRows = [...rows].sort((a, b) => b.total - a.total);
  const sortedDestinations = [...destinations].sort((a, b) => (totals[b.key] || 0) - (totals[a.key] || 0));

  const topRows = sortedRows.slice(0, MATRIX_TOP_N);
  const topDestinations = sortedDestinations.slice(0, MATRIX_TOP_N);
  const hasMoreRows = sortedRows.length > MATRIX_TOP_N;
  const hasMoreDestinations = sortedDestinations.length > MATRIX_TOP_N;
  const hasMore = hasMoreRows || hasMoreDestinations;
  const restrictedButtonStyle = viewOnly ? { opacity: 0.6, cursor: "not-allowed" } : undefined;
  const hasData = sortedRows.length > 0 && sortedDestinations.length > 0;

  return (
    <div className={classes.card}>
      <div className={classes.cardHeaderRow}>
        <div>
          <div className={classes.cardTitle}>Vehicle Dispatch matrix (Origin → Destination)</div>
          <div className={classes.cardSubtitle}>
            Displays the number of vehicles dispatched from each origin to each destination.
          </div>
        </div>
        {hasData && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              className={classes.fullScreenButton}
              style={restrictedButtonStyle}
              disabled={viewOnly}
              onClick={() => (viewOnly ? onRestrictedClick() : downloadDispatchMatrix(sortedRows, sortedDestinations))}
              title="Download as Excel"
            >
              ⬇ Download
            </button>
            {hasMore && (
              <button
                type="button"
                className={classes.fullScreenButton}
                style={restrictedButtonStyle}
                disabled={viewOnly}
                onClick={() => (viewOnly ? onRestrictedClick() : setExpanded(true))}
                title="View all"
              >
                ⛶ View all ({sortedRows.length} × {sortedDestinations.length})
              </button>
            )}
          </div>
        )}
      </div>

      {hasData ? (
        <MatrixTable destinations={topDestinations} rows={topRows} totals={totals} grandTotal={grandTotal} />
      ) : (
        <div className={classes.placeholderCard}>
          <div className={classes.placeholderTitle}>No data available</div>
          <div className={classes.placeholderText}>
            The Dispatch Matrix cannot be displayed until vehicle dispatch statuses are updated on the platform.
          </div>
        </div>
      )}

      {expanded && (
        <div className={classes.modalOverlay} onClick={() => setExpanded(false)}>
          <div
            className={`${classes.modalPanel} ${classes.modalPanelWide}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={classes.modalHeaderRow}>
              <div>
                <div className={classes.cardTitle}>Dispatch matrix — vehicles by origin → destination</div>
                <div className={classes.cardSubtitle}>
                  all {sortedRows.length} origins × {sortedDestinations.length} destinations
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className={classes.fullScreenButton}
                  style={restrictedButtonStyle}
                  disabled={viewOnly}
                  onClick={() => (viewOnly ? onRestrictedClick() : downloadDispatchMatrix(sortedRows, sortedDestinations))}
                  title="Download as Excel"
                >
                  ⬇ Download
                </button>
                <button
                  type="button"
                  className={classes.modalCloseButton}
                  onClick={() => setExpanded(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className={`${classes.modalScrollArea} ${classes.modalScrollAreaCompact}`}>
              <MatrixTable destinations={sortedDestinations} rows={sortedRows} totals={totals} grandTotal={grandTotal} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MODEL_PALETTE_SIZE = 8;
const MODELS_TOP_N = 5;
const modelPaletteClass = (index) => classes[`modelPalette${index % MODEL_PALETTE_SIZE}`];
const modelLegendSwatchClass = (index) => classes[`legendSwatch_modelPalette${index % MODEL_PALETTE_SIZE}`];

const downloadModelsByDestination = (rows) => {
  const exportRows = [];
  rows.forEach((row) => {
    Object.entries(row.segments).forEach(([model, qty]) => {
      exportRows.push({
        Destination: row.destination.name,
        Model: model,
        "Quantity (units)": qty,
      });
    });
  });

  if (!exportRows.length) {
    toast.error("Data is not available to download");
    return;
  }

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Models by Destination");
  XLSX.writeFile(wb, "models_by_destination.xlsx");
};

const ModelsByDestinationBars = ({ rows, models, maxTotal }) => (
  <div className={classes.barList}>
    {rows.map((row) => {
      const widthPct = Math.max((row.total / maxTotal) * 100, 6);
      const segments = Object.entries(row.segments);
      return (
        <div className={classes.barRow} key={row.destination.key}>
          <div
            className={classes.barLabel}
            title={[row.destination.address, row.destination.code].filter(Boolean).join(" | ") || row.destination.name}
          >
            {row.destination.name}
          </div>
          <div className={classes.barTrack} style={{ "--bar-width": `${widthPct}%` }}>
            <div className={classes.barTrackInner}>
              {segments.map(([model, qty]) => {
                const segPct = (qty / row.total) * 100;
                const modelIndex = models.indexOf(model);
                return (
                  <div
                    key={model}
                    className={modelPaletteClass(modelIndex)}
                    style={{ "--seg-width": `${segPct}%` }}
                    title={`${model}: ${qty.toLocaleString()} u`}
                  >
                    {segPct * widthPct >= 2500 && <span>{qty.toLocaleString()}</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.barValue}>{row.total.toLocaleString()} u</div>
        </div>
      );
    })}
  </div>
);

const ModelsByDestinationLegend = ({ models, rows }) => {
  const visibleModels = rows
    ? models.filter((model) => rows.some((row) => row.segments[model] != null))
    : models;
  return (
    <div className={`${classes.legend} ${classes.modelsLegendWrap}`}>
      {visibleModels.map((model) => {
        const index = models.indexOf(model);
        return (
          <span className={classes.legendItem} key={model} title={model}>
            <i className={modelLegendSwatchClass(index)} />
            {model.length > 28 ? `${model.slice(0, 28)}…` : model}
          </span>
        );
      })}
    </div>
  );
};

const ModelsByDestinationChart = ({ models, rows, viewOnly = false, onRestrictedClick = () => {} }) => {
  const [expanded, setExpanded] = useState(false);

  if (!rows.length) {
    return (
      <div className={classes.card}>
        <div className={classes.cardTitle} style={{ marginBottom: 10 }}>Model / Material Dispatch by Destination</div>
        <div className={classes.placeholderCard}>
          <div className={classes.placeholderTitle}>No data available</div>
          <div className={classes.placeholderText}>
            Model / Material dispatch details are available only for planned dispatches. No planning data was found.
          </div>
        </div>
      </div>
    );
  }

  const sortedRows = [...rows].sort((a, b) => b.total - a.total);
  const topRows = sortedRows.slice(0, MODELS_TOP_N);
  const hasMore = sortedRows.length > MODELS_TOP_N;
  const maxTotal = Math.max(...sortedRows.map((r) => r.total), 1);
  const restrictedButtonStyle = viewOnly ? { opacity: 0.6, cursor: "not-allowed" } : undefined;

  return (
    <div className={classes.card}>
      <div className={classes.cardHeaderRow}>
        <div>
          <div className={classes.cardTitle}>Model / Material Dispatch by Destination</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            className={classes.fullScreenButton}
            style={restrictedButtonStyle}
            disabled={viewOnly}
            onClick={() => (viewOnly ? onRestrictedClick() : downloadModelsByDestination(sortedRows))}
            title="Download as Excel"
          >
            ⬇ Download
          </button>
          {hasMore && (
            <button
              type="button"
              className={classes.fullScreenButton}
              style={restrictedButtonStyle}
              disabled={viewOnly}
              onClick={() => (viewOnly ? onRestrictedClick() : setExpanded(true))}
              title="View all"
            >
              ⛶ View all ({sortedRows.length})
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <ModelsByDestinationBars rows={topRows} models={models} maxTotal={maxTotal} />
      </div>
      <ModelsByDestinationLegend models={models} rows={topRows} />

      {expanded && (
        <div className={classes.modalOverlay} onClick={() => setExpanded(false)}>
          <div className={classes.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeaderRow}>
              <div>
                <div className={classes.cardTitle}>Model / Material Dispatch by Destination</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className={classes.fullScreenButton}
                  style={restrictedButtonStyle}
                  disabled={viewOnly}
                  onClick={() => (viewOnly ? onRestrictedClick() : downloadModelsByDestination(sortedRows))}
                  title="Download as Excel"
                >
                  ⬇ Download
                </button>
                <button
                  type="button"
                  className={classes.modalCloseButton}
                  onClick={() => setExpanded(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className={classes.modalScrollArea}>
              <ModelsByDestinationBars rows={sortedRows} models={models} maxTotal={maxTotal} />
            </div>
            <ModelsByDestinationLegend models={models} rows={sortedRows} />
          </div>
        </div>
      )}
    </div>
  );
};

const downloadVehiclesByStatus = (segments) => {
  const exportRows = segments
    .filter((s) => s.count > 0)
    .map((s) => ({
      Status: VEHICLE_STATUS_META[s.status].label,
      Vehicles: s.count,
      "Share (%)": s.pct,
    }));

  if (!exportRows.length) {
    toast.error("Data is not available to download");
    return;
  }

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vehicles by Status");
  XLSX.writeFile(wb, "vehicles_by_status.xlsx");
};

const VehiclesByStatusDonut = ({ statusTiles, total, viewOnly = false, onRestrictedClick = () => {} }) => {
  let cumulativePct = 0;
  const segments = VEHICLE_STATUS_ORDER.map((status) => {
    const count = statusTiles[status] || 0;
    const pct = total ? Math.round((count / total) * 100) : 0;
    const start = cumulativePct;
    cumulativePct += pct;
    return { status, count, pct, start, end: cumulativePct };
  });
  const nonEmptySegments = segments.filter((s) => s.count > 0);
  const lastIndex = nonEmptySegments.length - 1;
  const gradient = nonEmptySegments
    .map((s, i) => `var(--donut-${VEHICLE_STATUS_META[s.status].cssKey}) ${s.start}% ${i === lastIndex ? 100 : s.end}%`)
    .join(", ");
  const restrictedButtonStyle = viewOnly ? { opacity: 0.6, cursor: "not-allowed" } : undefined;

  return (
    <div className={classes.card}>
      <div className={classes.cardHeaderRow}>
        <div className={classes.cardTitle}>Vehicles Status</div>
        <button
          type="button"
          className={classes.fullScreenButton}
          style={restrictedButtonStyle}
          disabled={viewOnly}
          onClick={() => (viewOnly ? onRestrictedClick() : downloadVehiclesByStatus(segments))}
          title="Download as Excel"
        >
          ⬇ Download
        </button>
      </div>
      <div className={classes.donutRow}>
        <div className={classes.donutChart} style={{ "--donut-gradient": `conic-gradient(${gradient})` }}>
          {segments
            .filter((s) => s.count > 0)
            .map((s) => (
              <div
                key={s.status}
                className={classes.donutSegmentHover}
                style={{
                  "--seg-mask": `conic-gradient(transparent ${s.start}%, #000 ${s.start}%, #000 ${s.end}%, transparent ${s.end}%)`,
                }}
                title={`${VEHICLE_STATUS_META[s.status].label}: ${s.count} veh (${s.pct}%)`}
              />
            ))}
          <div className={classes.donutHole}>
            <div className={classes.donutHoleLabel}>total</div>
            <div className={classes.donutHoleValue}>{total} veh</div>
          </div>
        </div>
        <div className={classes.donutLegend}>
          {segments.map((s) => (
            <div
              className={classes.donutLegendItem}
              key={s.status}
              title={`${VEHICLE_STATUS_META[s.status].label}: ${s.count} veh (${s.pct}%)`}
            >
              <i className={classes[`legendSwatch_${VEHICLE_STATUS_META[s.status].cssKey}`]} />
              <span className={classes.donutLegendLabel}>{VEHICLE_STATUS_META[s.status].label}</span>
              <span className={classes.donutLegendPct}>{s.pct}%</span>
              <span className={classes.donutLegendCount}>{s.count} veh</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const handleExportVehicleAllocation = async (filters, setDownloading) => {
  setDownloading(true);
  try {
    const { download_url: downloadUrl } = await exportVehicleAllocationTable(filters);
    if (downloadUrl) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Data is not available to download");
    }
  } catch (err) {
    const message = err?.response?.data?.detail || err?.response?.data?.message || "Failed to download vehicle allocation";
    toast.error(message);
  } finally {
    setDownloading(false);
  }
};

const RowLevelTable = ({
  rows,
  pagination,
  onPageChange,
  exportFilters,
  viewOnly = false,
  onRestrictedClick = () => {},
}) => {
  const [downloading, setDownloading] = useState(false);
  const restrictedButtonStyle = viewOnly ? { opacity: 0.6, cursor: "not-allowed" } : undefined;
  const hasData = rows.length > 0;

  return (
  <div className={classes.card}>
    <div className={classes.cardHeaderRow}>
      <div>
          <div className={classes.cardTitle}>Vehicle Allocation</div>
      </div>
      {hasData && (
        <button
          type="button"
          className={classes.fullScreenButton}
          style={restrictedButtonStyle}
          disabled={viewOnly || downloading}
          onClick={() =>
            viewOnly ? onRestrictedClick() : handleExportVehicleAllocation(exportFilters, setDownloading)
          }
          title="Download as CSV"
        >
          {downloading ? "Downloading…" : "⬇ Download"}
        </button>
      )}
    </div>
    {hasData ? (
      <>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>Origin</th>
                <th style={{ textAlign: "center" }}>Destination</th>
                <th style={{ textAlign: "center" }}>Vehicle</th>
                <th style={{ textAlign: "center" }}>Transporter</th>
                <th style={{ textAlign: "center" }}>Models</th>
                <th style={{ textAlign: "center" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${pagination?.skip || 0}-${index}`}>
                  <td className={classes.plantCell} title={locationHoverText(row.origin)} style={{ whiteSpace: "normal" }}>
                    {truncateLane(locationName(row.origin))}
                  </td>
                  <td title={locationHoverText(row.destination)} style={{ whiteSpace: "normal" }}>
                    {truncateLane(locationName(row.destination)) || "—"}
                  </td>
                  <td>{row.vehicle_no}</td>
                  <td>{row.transporter_name}</td>
                  <td>
                    {row.models && row.models.length > 2
                      ? `${row.models.slice(0, 2).join(", ")} +${row.models.length - 2} more`
                      : (row.models || []).join(", ")}
                  </td>
                  <td>
                    <span className={`${classes.pill} ${badgeClassForStatus(row.status)}`}>
                      {VEHICLE_STATUS_META[row.status]?.label || row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && (
          <div className={classes.paginationBar}>
            <span>
              {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total_count)} of{" "}
              {pagination.total_count}
            </span>
            <button
              type="button"
              className={classes.paginationButton}
              disabled={pagination.skip === 0}
              onClick={() => onPageChange(Math.max(0, pagination.skip - pagination.limit))}
            >
              Prev
            </button>
            <button
              type="button"
              className={classes.paginationButton}
              disabled={pagination.skip + pagination.limit >= pagination.total_count}
              onClick={() => onPageChange(pagination.skip + pagination.limit)}
            >
              Next
            </button>
          </div>
        )}
      </>
    ) : (
      <div className={classes.placeholderCard}>
        <div className={classes.placeholderTitle}>No data available</div>
        <div className={classes.placeholderText}>
          The Vehicle Allocation cannot be displayed until the vehicle statuses are updated on the platform.
        </div>
      </div>
    )}
  </div>
  );
};

const LaneAggregateRows = ({ rows }) => (
  <div className={classes.tableWrap}>
    <table className={classes.table}>
      <thead>
        <tr>
          <th style={{ textAlign: "center" }}>Origin</th>
          <th style={{ textAlign: "center" }}>Destination</th>
          <th style={{ textAlign: "center" }}>Vehicles</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${locationName(row.origin)}-${locationName(row.destination)}`}>
            <td className={classes.plantCell} title={locationHoverText(row.origin)} style={{ whiteSpace: "normal" }}>
              {truncateLane(locationName(row.origin))}
            </td>
            <td title={locationHoverText(row.destination)} style={{ whiteSpace: "normal" }}>
              {truncateLane(locationName(row.destination)) || "—"}
            </td>
            <td style={{ textAlign: "center", fontWeight: 700 }}>{row.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const LaneAggregateTable = ({ rows, viewOnly = false, onRestrictedClick = () => {} }) => {
  const [expanded, setExpanded] = useState(false);
  const sortedRows = [...rows].sort((a, b) => b.count - a.count);
  const topRows = sortedRows.slice(0, TOP_N);
  const hasMore = sortedRows.length > TOP_N;
  const restrictedButtonStyle = viewOnly ? { opacity: 0.6, cursor: "not-allowed" } : undefined;

  return (
    <div className={classes.card}>
      <div className={classes.cardHeaderRow}>
        <div>
          <div className={classes.cardTitle}>Vehicle allocation — lane view</div>
          <div className={classes.cardSubtitle}>
            top {TOP_N} lanes by vehicle count · origin → destination for the selected period
          </div>
        </div>
        {hasMore && (
          <button
            type="button"
            className={classes.fullScreenButton}
            style={restrictedButtonStyle}
            disabled={viewOnly}
            onClick={() => (viewOnly ? onRestrictedClick() : setExpanded(true))}
            title="View all"
          >
            ⛶ View all ({sortedRows.length})
          </button>
        )}
      </div>

      <LaneAggregateRows rows={topRows} />

      {expanded && (
        <div className={classes.modalOverlay} onClick={() => setExpanded(false)}>
          <div className={classes.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeaderRow}>
              <div>
                <div className={classes.cardTitle}>Vehicle allocation — lane view</div>
                <div className={classes.cardSubtitle}>all {sortedRows.length} lanes</div>
              </div>
              <button
                type="button"
                className={classes.modalCloseButton}
                onClick={() => setExpanded(false)}
              >
                ✕
              </button>
            </div>
            <div className={classes.modalScrollArea}>
              <LaneAggregateRows rows={sortedRows} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VehicleAllocationTab = ({
  vehicleAllocation,
  table,
  onTablePageChange,
  exportFilters,
  viewOnly = false,
  onRestrictedClick = () => {},
}) => {
  const totalVehicles = VEHICLE_STATUS_ORDER.reduce(
    (sum, status) => sum + (vehicleAllocation.statusTiles[status] || 0),
    0
  );

  const hasNoData =
    totalVehicles === 0 &&
    vehicleAllocation.dispatchMatrix.length === 0 &&
    (!table || table.rows.length === 0);

  if (hasNoData) {
    return (
      <div className={classes.placeholderCard}>
        <div className={classes.placeholderTitle}>No data available for this period</div>
        <div className={classes.placeholderText}>
          Please change the period or filters to see vehicle allocation activity.
        </div>
      </div>
    );
  }

  return (
    <>
      <StatusTilesRow statusTiles={vehicleAllocation.statusTiles} />

      <DispatchMatrix
        destinations={vehicleAllocation.destinations}
        rows={vehicleAllocation.dispatchMatrix}
        totals={vehicleAllocation.matrixTotals}
        grandTotal={vehicleAllocation.matrixGrandTotal}
        viewOnly={viewOnly}
        onRestrictedClick={onRestrictedClick}
      />

      <div className={classes.twoColRow}>
        <ModelsByDestinationChart
          models={vehicleAllocation.modelsByDestination.models}
          rows={vehicleAllocation.modelsByDestination.rows}
          viewOnly={viewOnly}
          onRestrictedClick={onRestrictedClick}
        />
        <VehiclesByStatusDonut
          statusTiles={vehicleAllocation.statusTiles}
          total={totalVehicles}
          viewOnly={viewOnly}
          onRestrictedClick={onRestrictedClick}
        />
      </div>

      {table && table.mode === "row_level" && (
        <RowLevelTable
          rows={table.rows}
          pagination={table.pagination}
          onPageChange={onTablePageChange}
          exportFilters={exportFilters}
          viewOnly={viewOnly}
          onRestrictedClick={onRestrictedClick}
        />
      )}
      {table && table.mode === "lane_aggregate" && (
        <LaneAggregateTable rows={table.rows} viewOnly={viewOnly} onRestrictedClick={onRestrictedClick} />
      )}
    </>
  );
};

export default VehicleAllocationTab;
